import { LeadAction } from "@/api/app-configuration.api";
import {
  getCampaignQueryOptions,
  simpleCampaignsQueryOptions,
} from "@/api/campaign.api";
import { getCompanyQueryOptions } from "@/api/company.api";
import { createLeadContactQueryOptions } from "@/api/contacts.api";
import {
  createLeadQueryOptions,
  createLeadsQueryOptions,
  LeadNotes,
  leadsApi,
  ListLeadsParams,
  listLeadsParamsSchema,
  useAddLeadNoteMutation,
  useDeleteLeadNoteMutation,
  useEditLeadNoteMutation,
  useLeadsNotesQuery,
} from "@/api/leads.api";
import { createUsersQueryOptions } from "@/api/users.api";
import { IntegrationForm } from "@/components/forms/IntegrationForm";
import { Button } from "@/components/generic/Button";
import { Modal } from "@/components/generic/Modal";
import { Popover } from "@/components/generic/Popover";
import { Separator } from "@/components/generic/Separator";
import { Sheet, SheetBody, SheetContent } from "@/components/generic/Sheet";
import { PageHeader } from "@/components/layout/PageHeader";
import { Sidebar, SidebarContent } from "@/components/layout/Sidebar";
import { ActionBar } from "@/components/lead/ActionBar";
import { useCountdown } from "@/hooks/use-countdown";
import { getLeads, useLeads } from "@/hooks/use-leads";
import { useScrollAtBottom } from "@/hooks/use-scroll-at-bottom";
import { LeadNavigationProvider } from "@/providers/LeadNavigationProvider";
import { useScreenSize } from "@/providers/ScreenSizeProvider";
import {
  getIntegrationInputQueryOptions,
  useAvailableProvidersQuery,
} from "@/queries/integrations";
import { isAdmin, useAuth } from "@/services/auth.service";
import {
  createEditorLockPingQueryOptions,
  useEditorLock,
} from "@/services/editor-lock.service";
import { useZoneMode } from "@/services/zone.service";
import { Lead, LeadFeedback } from "@/types/campaign";
import {
  ProviderErrorEnum,
  ThirdPartyImportResult,
} from "@/types/integrations";
import { createNamedErrorType, LassoErrorCode } from "@/types/lasso-exception";
import { api, LassoException, PagedResult } from "@/utils/api";
import { formatDateTime } from "@/utils/dates";
import { formatExportInputs } from "@/utils/integration-helpers";
import {
  InfiniteData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  notFound,
  Outlet,
  redirect,
  ToOptions,
  useMatchRoute,
} from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { atom, getDefaultStore, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { HTTPError } from "ky";
import { omit } from "lodash-es";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Ellipsis,
  Fullscreen,
  Loader2,
  NotebookPen,
  Pencil,
  Trash,
  X,
} from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  DialogTrigger,
  Button as RAButton,
  TextArea,
} from "react-aria-components";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { Tooltip } from "../../components/generic/Tooltip";

const LassoDuplicateActionException = createNamedErrorType({
  DuplicateAction: () => "Duplikat registrering blev fundet.",
});

// TODO - fix this zod type, I'd prefer to have typings on the routes but Zod won't allow it :(
const searchParamsSchema = listLeadsParamsSchema
  .pick({
    name: true,
    configurationName: true,
    status: true,
    responsibleId: true,
    campaignId: true,
    orderBy: true,
    desc: true,
  })
  .extend({
    createdBefore: z.iso.datetime().optional(),
    returnTo: z.union([z.literal("leads"), z.literal("campaign")]).optional(),
    embed: z.boolean().optional(),
  });

const createdBeforeAtom = atom<Date | null>(null);
const previousLeadIdAtom = atom<number>();
const countdownAtom = atomWithStorage<number>(
  "zoneCountdown",
  1800,
  undefined,
  {
    getOnInit: true,
  },
);

const MotionClock = motion.create(Clock);
const MotionFullscreen = motion.create(Fullscreen);
const MotionNote = motion.create(NotebookPen);

function buildReturnLink(
  search: z.infer<typeof searchParamsSchema>,
  leadsCreatedBefore: Date | undefined,
): ToOptions {
  if (search.returnTo) {
    switch (search.returnTo) {
      case "leads": {
        return {
          to: "/leads",
          search: {
            ...omit(search, ["returnTo"]),
            createdBefore: leadsCreatedBefore,
          },
        };
      }

      case "campaign": {
        const campaignId =
          search.campaignId?.length === 1 ? search.campaignId[0] : undefined;

        if (campaignId) {
          return {
            to: "/campaigns/$id",
            params: { id: campaignId },
            search: {
              ...omit(search, ["campaignId", "returnTo", "createdBefore"]),
              leadsCreatedBefore: leadsCreatedBefore?.toISOString(),
            },
          };
        }

        break;
      }
    }
  }

  return { to: "/" };
}

export const Route = createFileRoute("/player")({
  component: RouteComponent,
  params: z.object({
    id: z.string().optional(),
  }),
  validateSearch: searchParamsSchema.parse,
  beforeLoad: async ({ context, params, search, cause }) => {
    if (context.trialPeriod?.isExpired) {
      throw redirect({
        ...buildReturnLink(
          search,
          search.createdBefore ? new Date(search.createdBefore) : undefined,
        ),
        resetScroll: false,
      });
    }

    const jotaiDefaultStore = getDefaultStore();

    const usersQueryOptions = createUsersQueryOptions({ nameFilter: "" });

    let leadsQueryParams:
      | Parameters<typeof createLeadsQueryOptions>[0]
      | undefined = undefined;

    let leadQueryOptions = queryOptions({
      ...createLeadQueryOptions(0),
      enabled: false,
    });

    let campaignQueryOptions = getCampaignQueryOptions();

    const getCompanyView = async (lassoId: string) => {
      try {
        const companyQueryOptions = getCompanyQueryOptions(lassoId);
        const company = await context.queryClient.ensureQueryData(
          getCompanyQueryOptions(lassoId),
        );
        return {
          type: "company" as const,
          companyQueryOptions: queryOptions({
            ...companyQueryOptions,
            initialData: company,
          }),
          company,
        };
      } catch (reason) {
        if (reason instanceof HTTPError) {
          const response = await reason.response.json<LassoException>();
          if (
            reason.response.status === 400 &&
            response.errorCode === LassoErrorCode.InvalidArgumentError
          )
            throw notFound({ data: { resource: "firmaet" } });
        }
        throw reason;
      }
    };

    const getLeadView = async (id: number | undefined) => {
      if (cause !== "preload" && !search.embed) {
        // Hide createdBefore from search and update atom
        if (search.createdBefore) {
          jotaiDefaultStore.set(
            createdBeforeAtom,
            new Date(search.createdBefore),
          );
          throw redirect({
            replace: true,
            search: (prev) => omit(prev, ["createdBefore"]),
          });
        }

        // Reset createdBefore if not in search
        if (!jotaiDefaultStore.get(createdBeforeAtom)) {
          jotaiDefaultStore.set(createdBeforeAtom, new Date());
        }
      }

      const createdBefore =
        jotaiDefaultStore.get(createdBeforeAtom) ?? new Date();

      if (!search.embed) {
        leadsQueryParams = {
          ...search,
          createdBefore,
        };
      }

      const leads = leadsQueryParams
        ? await getLeads({
            params: leadsQueryParams,
          })
        : [];

      if (id === undefined) {
        const [firstLead = null] = leads;
        if (!firstLead) throw new Error("No lead id");
        throw redirect({
          replace: true,
          to: "/player/$id",
          params: { id: firstLead.id },
          search,
        });
      }

      leadQueryOptions = createLeadQueryOptions(id);

      const leadResponse =
        await context.queryClient.ensureQueryData(leadQueryOptions);

      campaignQueryOptions = getCampaignQueryOptions(
        leadResponse.lead.campaignId,
      );

      const campaign =
        await context.queryClient.ensureQueryData(campaignQueryOptions);

      campaignQueryOptions = {
        ...campaignQueryOptions,
        initialData: campaign,
      };

      const companyQueryOptions = getCompanyQueryOptions(
        leadResponse.lead.lassoId,
      );

      if (cause === "enter") {
        getDefaultStore().set(previousLeadIdAtom, id);
      }

      if (cause !== "preload") {
        context.queryClient.ensureInfiniteQueryData(usersQueryOptions);
        context.queryClient.ensureQueryData(simpleCampaignsQueryOptions);
      }

      await context.queryClient.ensureQueryData(
        createEditorLockPingQueryOptions({
          roomId: context.user.lassoOrganizationId,
        }),
      );

      return {
        type: "lead" as const,
        createdBefore,
        leads,
        leadResponse,
        campaign,
        companyQueryOptions,
        leadId: id,
        previousLeadId: jotaiDefaultStore.get(previousLeadIdAtom),
        simpleLead: leadResponse.lead,
      };
    };

    let entityId: number | string | undefined = params.id;
    if (entityId !== undefined && !/CVR-1-\d{8}/i.test(entityId)) {
      entityId = parseInt(entityId);
    }

    const view =
      typeof entityId === "string"
        ? await getCompanyView(entityId)
        : await getLeadView(entityId);

    // try {
    //   await context.queryClient.ensureQueryData(companyQueryOptions);
    // } catch (reason) {
    //   if (reason instanceof HTTPError && reason.response.status === 404) {
    //     matches.at(-1)!.staticData.playerActive = false;
    //     throw notFound({ data: { resource: "firmaet" } });
    //   }
    // }

    return {
      id: params.id,
      view,
      companyView: view.type === "company" ? view : undefined,
      leadView: view.type === "lead" ? view : undefined,
      usersQueryOptions,
      simpleCampaignsQueryOptions,
      useUsersQuery: () => useInfiniteQuery(usersQueryOptions),
      useSimpleCampaignsQuery: () => useQuery(simpleCampaignsQueryOptions),
      useCompanyQuery: () => useQuery(view.companyQueryOptions),
      useLeads: () =>
        useLeads(
          leadsQueryParams
            ? {
                params: leadsQueryParams,
              }
            : {
                enabled: false,
              },
        ),
      useLeadQuery: () => useQuery(leadQueryOptions),
      useCampaignQuery: () => useQuery(campaignQueryOptions),
    };
  },
  staticData: {
    meta: {
      isTitleRoot: true,
      title: "Leadkort",
    },
  },
  loader: async ({ context }) => {
    return {
      meta: {
        title: context.view
          ? context.view.type === "lead"
            ? `${context.view.simpleLead.name} - Leadkort`
            : `${context.view.company.name} - Leadkort`
          : "Leadkort",
      },
    };
  },

  onLeave: () => {
    getDefaultStore().set(createdBeforeAtom, null);
    getDefaultStore().set(previousLeadIdAtom, undefined);
  },
});

interface NoteItemProps {
  note: LeadNotes;
  isFirst: boolean;
  isEditing: boolean;
  isOwner: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const NoteItem = memo(function NoteItem({
  note,
  isFirst,
  isEditing,
  isOwner,
  onEdit,
  onDelete,
}: NoteItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  const isOptimisticNote = note.id.startsWith("temp-");

  const handleEdit = useCallback(() => {
    onEdit();
    setIsOpen(false);
  }, [onEdit]);

  return (
    <div className={twMerge("w-full px-4", !isFirst && "pt-2")}>
      <div
        className={twMerge(
          "flex flex-col rounded-sm border border-transparent px-2 py-2 transition-all duration-300 xl:px-4 xl:py-4",
          isEditing && "border-theme-primary",
          isOptimisticNote && "opacity-50",
        )}
      >
        <div className="flex w-full justify-between pb-1 xl:items-center xl:pb-3">
          <div className="flex flex-col">
            <div className="flex flex-col gap-1 xl:flex-row xl:gap-2">
              <div className="flex w-full max-w-35">
                <span className="overflow-hidden text-xs font-bold text-ellipsis whitespace-nowrap">
                  {note.name}
                </span>
              </div>
              <span className="text-theme-on-glass-variant w-full text-[11px] font-normal">
                {formatDateTime(new Date(note.timestamp), {
                  hour: "numeric",
                  minute: "numeric",
                  day: "2-digit",
                  month: "2-digit",
                })}
              </span>
            </div>
          </div>
          {isOwner && !isOptimisticNote && (
            <div>
              <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
                <RAButton
                  className="group cursor-pointer"
                  isDisabled={isEditing}
                >
                  <Ellipsis
                    size={16}
                    className={twMerge(
                      "text-theme-on-glass-variant group-hover:text-theme-on-glass transition",
                      isOpen && "text-theme-on-glass",
                    )}
                  />
                </RAButton>
                <Popover placement="bottom" className="w-30 rounded-lg">
                  <div className="flex flex-col gap-5 p-4 shadow-lg">
                    <RAButton
                      onPress={handleEdit}
                      className="text-theme-on-glass flex cursor-pointer items-center gap-2"
                    >
                      <Pencil size={16} />
                      <span className="text-xs font-medium">Rediger</span>
                    </RAButton>
                    <RAButton
                      onPress={onDelete}
                      className="text-theme-on-glass flex cursor-pointer items-center gap-2"
                    >
                      <Trash size={16} />
                      <span className="text-xs font-medium">Slet</span>
                    </RAButton>
                  </div>
                </Popover>
              </DialogTrigger>
            </div>
          )}
        </div>
        <div>
          <span className="text-xs font-normal wrap-break-word whitespace-pre-wrap">
            {note.content}
          </span>
        </div>
      </div>
    </div>
  );
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const params = Route.useParams();

  const matchRoute = useMatchRoute();

  const pendingRoute = matchRoute({
    to: "/player/$id",
    pending: true,
    fuzzy: true,
  });

  const [, setPreviousLeadId] = useAtom(previousLeadIdAtom);
  const [notesOpen, setNotesOpen] = useState(false);

  const [notesScrollEl, setNotesScrollEl] = useState<HTMLElement | null>(null);

  const {
    isAtBottom,
    canScroll: canScrollNotes,
    isAtTop,
  } = useScrollAtBottom(notesScrollEl, { enabled: notesOpen });

  const {
    leads,
    leadsQuery: { isFetching: isFetchingLeads, fetchNextPage, hasNextPage },
  } = context.useLeads();

  const { data: lead } = context.useLeadQuery();
  const { data: campaign } = context.useCampaignQuery();
  const { data: simpleCampaigns } = context.useSimpleCampaignsQuery();
  const contactPurchased = useMemo(() => lead?.lead.contactPurchased, [lead]);

  const contactQueryOptions = useMemo(() => {
    return createLeadContactQueryOptions(
      String(lead?.lead.id),
      context.leadView?.simpleLead.contact?.id ?? undefined,
      contactPurchased,
    );
  }, [
    context.leadView?.simpleLead.contact?.id,
    lead?.lead.id,
    contactPurchased,
  ]);

  const { data: contact, isFetching: isFetchingContact } =
    useQuery(contactQueryOptions);
  const campaignsById = useMemo(
    () => new Map(simpleCampaigns?.map((c) => [c.id, c]) ?? []),
    [simpleCampaigns],
  );

  const leadsIndexesById = useMemo(
    () => new Map(leads.map((lead, index) => [lead.id, index])),
    [leads],
  );

  const leadsIndex = useMemo(
    () =>
      (context.leadView && leadsIndexesById.get(context.leadView.leadId)) ?? -1,
    [context.leadView, leadsIndexesById],
  );

  const [zoneMode, setZoneMode] = useZoneMode();
  const [countdownValue, setCountdownValue] = useAtom(countdownAtom);
  const { seconds, start, resetTo } = useCountdown({
    initialSeconds: countdownValue,
    onComplete() {
      setZoneMode(false);
      setCountdownValue(1800);
    },
  });

  const [openLeadActivityModal, setOpenLeadActivityModal] = useState(false);
  const [
    temporarilyStoreLeadActionParams,
    setTemporarilyStoreLeadActionParams,
  ] = useState<{
    status: Lead["status"];
    action?: Lead["statusReason"];
    date?: Lead["followUpTime"] | LeadAction["presetFollowUp"];
    feedback?: LeadFeedback;
  } | null>(null);
  const [isSubmittingLeadActivity, setIsSubmittingLeadActivity] =
    useState(false);

  const { data: inputs } = useQuery(
    getIntegrationInputQueryOptions(
      campaign?.exportSystem ?? undefined,
      campaign?.exportMode ?? undefined,
    ),
  );

  const shouldExportLead = useMemo(() => {
    return !!campaign?.exportSystem && campaign.exportMode === "SalesManual";
  }, [campaign]);

  const showActivityDialog = useMemo(() => {
    if (!inputs || !campaign) return false;
    if (
      (!campaign.exportSystem && !campaign.exportMode) ||
      !campaign.exportInput
    )
      return false;
    if (
      "createengagementdropdown" in campaign.exportInput &&
      campaign.exportInput?.createengagementdropdown !== "never"
    )
      return true;
    return false;
  }, [inputs, campaign]);

  // Prefetch next page when we're close to the end
  useEffect(() => {
    if (
      !search.embed &&
      !isFetchingLeads &&
      hasNextPage &&
      leads.length > 0 &&
      leadsIndex >= 0 &&
      leadsIndex >= leads.length - 3
    ) {
      fetchNextPage();
    }
  }, [
    leadsIndex,
    search.embed,
    fetchNextPage,
    hasNextPage,
    leads.length,
    isFetchingLeads,
  ]);

  const goToLeadById = useCallback(
    (id: number) => {
      if (!context.leadView || pendingRoute) return;
      setPreviousLeadId(context.leadView.leadId);
      navigate({
        to: "/player/$id",
        params: { id },
        search,
        resetScroll: true,
      });
    },
    [context.leadView, navigate, pendingRoute, search, setPreviousLeadId],
  );

  const navigateBack = useCallback(() => {
    if (!context.leadView?.createdBefore) return;

    if (zoneMode) {
      const confirmed = confirm(
        "Du afslutter nu fokus-tilstand. Er du sikker?",
      );
      if (!confirmed) return;
    }

    setZoneMode(false);
    setCountdownValue(1800);
    stop();
    resetTo(1800);

    navigate({
      ...buildReturnLink(search, context.leadView.createdBefore),
      resetScroll: false, // Keep scroll position when returning to leads/campaigns
    });
  }, [
    context.leadView?.createdBefore,
    search,
    navigate,
    zoneMode,
    setZoneMode,
    setCountdownValue,
    resetTo,
  ]);

  const goToNextLead = useCallback(
    async (closeOnEnd: boolean = false) => {
      setNotesOpen(false);
      if (!context.leadView || pendingRoute) return;

      if (leadsIndex < leads.length - 1) {
        const nextIndex = leadsIndex + 1;

        setPreviousLeadId(context.leadView.leadId);
        navigate({
          to: "/player/$id",
          params: { id: leads[nextIndex]?.id },
          search,
          resetScroll: true,
        });
      } else if (closeOnEnd) {
        toast.success("Du har netop behandlet det sidste lead i listen");
        navigateBack();
      }
    },
    [
      context.leadView,
      pendingRoute,
      leadsIndex,
      leads,
      setPreviousLeadId,
      navigate,
      search,
      navigateBack,
    ],
  );

  const goToPreviousLead = useCallback(() => {
    setNotesOpen(false);
    if (!context.leadView || pendingRoute) return;

    if (leadsIndex <= 0 || !leads.length) return;

    setPreviousLeadId(context.leadView.leadId);
    navigate({
      to: "/player/$id",
      params: { id: leads[leadsIndex - 1]?.id },
      search,
      resetScroll: true,
    });
  }, [
    context.leadView,
    pendingRoute,
    leadsIndex,
    leads,
    setPreviousLeadId,
    navigate,
    search,
  ]);

  // Add keyboard navigation
  useEffect(() => {
    if (!context.leadView?.createdBefore) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Disable keyboard navigation in embed mode
      if (search.embed) return;

      // Don't trigger navigation if user is typing in an input
      if (
        ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName || "")
      ) {
        return;
      }

      // Navigate to previous lead with left arrow
      if (event.key === "ArrowLeft") goToPreviousLead();

      // Navigate to next lead with right arrow
      if (event.key === "ArrowRight") goToNextLead();

      // Navigate back with Escape key
      if (event.key === "Escape") {
        if (notesOpen) {
          setNotesOpen(false);
          return;
        }
        navigateBack();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    navigateBack,
    search,
    goToNextLead,
    goToPreviousLead,
    context.leadView?.createdBefore,
    notesOpen,
  ]);

  // Update the atom when countdown changes
  useEffect(() => {
    setCountdownValue(seconds);
  }, [seconds, setCountdownValue]);

  const formattedCount = useMemo(() => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }, [seconds]);

  useEffect(() => {
    if (seconds === 0) {
      // Reset to 30 minutes if timer expired
      setCountdownValue(1800);
      setZoneMode(false);
    }
  }, [setZoneMode, setCountdownValue, seconds]);

  useEffect(() => {
    if (zoneMode) start();
  }, [zoneMode, start]);

  const toggleZoneMode = () => {
    setZoneMode(!zoneMode);
    if (zoneMode) {
      stop();
      setCountdownValue(1800);
      resetTo(1800);
    } else {
      toast(() => <b>30 minutters fokus - starter nu!</b>);
      setCountdownValue(1800);
      requestAnimationFrame(() => {
        start();
      });
    }
  };

  const { mutateAsync: updateLead } = useMutation({
    mutationFn: (lead: Partial<Lead>) => {
      if (!lead.id) return Promise.reject("Lead ID is required");
      return leadsApi.put(lead.id, lead);
    },
    onSuccess: (data) => {
      context.queryClient.setQueryData(["lead", data.lead.id], data);

      const leadsQueries = context.queryClient.getQueriesData<
        InfiniteData<PagedResult<Lead>>
      >({
        queryKey: ["leads"],
      });

      for (const [queryKey, queryData] of leadsQueries) {
        if (!queryData) continue;

        const params = queryKey[1] as ListLeadsParams | undefined;

        let leadFound = false;

        const newPages = queryData.pages?.map((page) => {
          const leadIndex = page.results.findIndex(
            (l) => l.id === data.lead.id,
          );

          if (leadIndex === -1) return page;

          leadFound = true;
          const newResults = [...page.results];

          const removeFromQuery = () => {
            newResults.splice(leadIndex, 1);
            return {
              ...page,
              results: newResults,
              resultsReturned: Math.max(0, page.resultsReturned - 1),
              resultsFound: Math.max(0, page.resultsFound - 1),
            };
          };

          if (params) {
            if (params.status?.length) {
              const statuses = new Set(params.status);
              if (!statuses.has(data.lead.status)) {
                return removeFromQuery();
              }
            }

            if (params.campaignId?.length) {
              const campaignIds = new Set(params.campaignId);
              if (!campaignIds.has(data.lead.campaignId)) {
                return removeFromQuery();
              }
            }

            if (params.configurationName?.length) {
              const campaign = campaignsById.get(data.lead.campaignId);
              if (!campaign) return removeFromQuery();

              const configuration = campaign.configurations.find(
                (c) => c.id === data.lead.configurationId,
              );
              if (!configuration?.name) return removeFromQuery();

              const configNames = new Set(params.configurationName);
              if (!configNames.has(configuration.name)) {
                return removeFromQuery();
              }
            }
          }

          newResults.splice(leadIndex, 1, data.lead);
          return {
            ...page,
            results: newResults,
          };
        });

        if (leadFound) {
          context.queryClient.setQueryData(queryKey, {
            ...queryData,
            pages: newPages,
          });
        }
      }

      context.queryClient.invalidateQueries({
        queryKey: ["campaign-leads-overview"],
        predicate: ({ queryKey }) => {
          const [, ids = null] = queryKey;
          if (Array.isArray(ids)) {
            return ids.includes(data.lead.campaignId);
          }
          return false;
        },
      });

      context.queryClient.invalidateQueries({
        queryKey: ["activitiesStatistics"],
      });
    },
  });

  const updateLeadStatus = (
    status: Lead["status"],
    action?: Lead["statusReason"],
    date?: Lead["followUpTime"] | LeadAction["presetFollowUp"],
    feedback?: LeadFeedback,
    data?: Record<string, unknown>,
    ignoreDialog: boolean = false,
  ) => {
    if (!lead) return;
    const leadId = lead.lead.id;
    if (showActivityDialog && !ignoreDialog) {
      setTemporarilyStoreLeadActionParams({ status, action, date, feedback });
      setOpenLeadActivityModal(true);
      return;
    }
    if (openLeadActivityModal && !ignoreDialog) setOpenLeadActivityModal(false);
    const updateStatus = () => {
      const dateObj = new Date(date ?? "");
      return updateLead({
        ...lead.lead,
        status,
        statusReason: action ?? null,
        followUpTime: date ? dateObj.toISOString() : null,
        feedback,
      });
    };

    goToNextLead(true);

    const onDismiss = async () => {
      toast.loading("Opdaterer lead...", {
        id: `lead-promise-${leadId}`,
      });
      await updateStatus();
      // Export lead if status is Qualified and either data is provided or shouldExportLead is true
      if ((data || shouldExportLead) && status === "Qualified") {
        const exportLeadPromise = api
          .post<ThirdPartyImportResult>(
            `apps/sales/leads/${lead.lead.id}/export/${campaign?.exportSystem}`,
            {
              json: {
                input: {
                  ...campaign?.exportInput,
                  ...(data ? formatExportInputs(data) : {}),
                },
                contact: lead.lead.contact ? contact : null,
              },
            },
          )
          .json()
          .then((result) => {
            if (result.success) return result;
            if (
              result.error &&
              result.error.type === ProviderErrorEnum.Duplicate
            ) {
              if (result.relatedResults.some((r) => r.success)) {
                return result;
              } else {
                throw new LassoDuplicateActionException("DuplicateAction");
              }
            }
            throw new Error();
          });
        toast.promise(exportLeadPromise, {
          id: `lead-promise-${leadId}`,
          loading: "Eksporterer lead...",
          success: "Lead eksporteret",
          error: "Noget gik galt under eksport. Prøv igen senere.",
        });
      } else {
        toast.success("Lead opdateret", {
          id: `lead-promise-${leadId}`,
        });
      }
    };

    toast(
      () => (
        <>
          Lead markeret som{" "}
          <span className="font-bold">{action ?? status}</span>
        </>
      ),
      {
        id: `lead-status-updated-${leadId}`,
        cancelButtonStyle: {
          backgroundColor: "linear-gradient(var(--theme-gradient))",
        },
        cancel: {
          label: "Fortryd",
          onClick: () => {
            goToLeadById(leadId);
          },
        },
        onDismiss,
        onAutoClose: onDismiss,
      },
    );
  };

  const usersQuery = context.useUsersQuery();
  const users = useMemo(
    () => usersQuery.data?.pages.flatMap((page) => page.results) ?? [],
    [usersQuery.data?.pages],
  );
  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );

  const editorLock = useEditorLock({
    enabled: !!context.leadView,
    editorLockId: context.leadView
      ? `sales/lead/${context.leadView.leadId}`
      : "",
  });

  const { isEditor, isEditorInOtherSession } = editorLock;

  const currentEditor = useMemo(() => {
    if (!editorLock.currentEditor) return null;
    const { userId, sessionId } = editorLock.currentEditor;
    const user = usersById.get(userId);
    return { userId, sessionId, user };
  }, [editorLock.currentEditor, usersById]);

  const [textareaValue, setTextareaValue] = useState("");

  const leadId = context.leadView?.leadId;
  const { data: notesData } = useLeadsNotesQuery(leadId ?? 0);

  const { mutate: addLeadNote } = useAddLeadNoteMutation(leadId ?? 0);

  const { mutate: editLeadNote } = useEditLeadNoteMutation(leadId ?? 0);

  const { mutate: deleteLeadNote } = useDeleteLeadNoteMutation(leadId ?? 0);

  const hasStorageError = useMemo(
    () => notesData?.error?.errorCode === LassoErrorCode.InvalidArgumentError,
    [notesData],
  );
  const notes = useMemo(() => notesData?.notes ?? [], [notesData?.notes]);

  const { data: availableProviders = [] } = useAvailableProvidersQuery(true);

  const isStorageConnected = useMemo(() => {
    return availableProviders.some((provider) => provider.capabilities.storage);
  }, [availableProviders]);

  const [editId, setEditId] = useState<Map<string, LeadNotes> | null>(null);
  const [openTextArea, setOpenTextArea] = useState(false);

  const { user } = useAuth();

  const { isWithinResponsiveRange } = useScreenSize();

  const handleEditNote = useCallback(
    (note: LeadNotes) => {
      setTextareaValue(note.content);
      setEditId(new Map([[note.id, note]]));
      if (isWithinResponsiveRange) {
        setOpenTextArea(true);
      }
    },
    [isWithinResponsiveRange],
  );

  const handleDeleteNote = useCallback(
    (noteId: string) => {
      deleteLeadNote(noteId, {
        onError: () => toast.error("Fejl ved sletning af note"),
      });
    },
    [deleteLeadNote],
  );

  const sendNewNote = useCallback(() => {
    if (textareaValue.trim().length === 0 || !leadId) return;
    addLeadNote(
      { content: textareaValue, userName: user.name.trim() },
      {
        onError: (reason) => {
          if (reason instanceof HTTPError && reason.response.status === 507) {
            toast.error("Din integration har ikke mere lagerplads", {
              description:
                "Anskaf mere plads hos din integration for at kunne tilføje flere noter eller skift til en anden integration.",
            });
            return;
          }
          toast.error("Fejl ved tilføjelse af note");
        },
      },
    );
    setTextareaValue("");
    setOpenTextArea(false);
  }, [textareaValue, leadId, addLeadNote, user.name]);

  const editingNote = useCallback(() => {
    if (!editId || editId.size === 0) return;
    const [[noteId, noteData]] = editId.entries();

    editLeadNote(
      {
        id: noteId,
        content: textareaValue.trim(),
        timestamp: new Date().toISOString(),
        name: noteData.name,
        userId: noteData.userId,
        shared: noteData.shared,
        orgId: noteData.orgId,
      },
      {
        onError: (reason) => {
          if (reason instanceof HTTPError && reason.response.status === 507) {
            toast.error("Din integration har ikke mere lagerplads", {
              description:
                "Anskaf mere plads hos din integration for at kunne tilføje flere noter eller skift til en anden integration.",
            });
            return;
          }
          toast.error("Fejl ved opdatering af note");
        },
      },
    );
    setTextareaValue("");
    setEditId(null);
    setOpenTextArea(false);
  }, [editId, textareaValue, editLeadNote]);

  const disableSaveNote = useMemo(() => {
    const trimmedValue = textareaValue.trim();
    if (trimmedValue.length === 0) return true;
    if (!editId) return false;

    const firstNote = editId.values().next().value;
    return trimmedValue === firstNote?.content;
  }, [editId, textareaValue]);

  useLayoutEffect(() => {
    setNotesOpen(false);
  }, [params.id]);

  return (
    <LeadNavigationProvider
      onNextLead={goToNextLead}
      onPreviousLead={goToPreviousLead}
      onNavigateBack={navigateBack}
      notesOpen={notesOpen}
      onToggleNotes={() => setNotesOpen(!notesOpen)}
    >
      <div className="-m-7 flex flex-col">
        <div className="grid shrink-0 grow overflow-hidden *:col-start-1 *:row-start-1">
          <AnimatePresence mode="sync">
            <Outlet key={params.id} />
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {notesOpen && (
            <motion.div
              key="notes-sidebar"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.1, ease: [0.4, 0, 0.2, 1] }}
              className={twMerge(
                "fixed inset-y-0 z-20 flex py-3.5 xl:py-7",
                isWithinResponsiveRange ? "mb-27.5" : "mb-24",
              )}
              style={{
                right: `${isWithinResponsiveRange ? `max(0.7rem, calc((100vw - 77rem) / 2 + 2rem))` : "max(1.6rem, calc((100vw - 100rem) / 2 + 3rem))"}`,
              }}
            >
              <Sidebar
                direction="right"
                className={twMerge(
                  isWithinResponsiveRange ? "min-w-60" : "min-w-80",
                )}
              >
                <SidebarContent className="flex h-full flex-col pt-7.5 xl:pt-14">
                  <div className="z-20 px-6 xl:px-8">
                    <div className="flex items-center justify-between pb-7.5 xl:pb-10.5">
                      <h2 className="text-xs font-medium uppercase">Noter</h2>
                      <RAButton
                        onPress={() => setNotesOpen(false)}
                        className="hover:text-theme-active cursor-pointer"
                      >
                        <X className="size-6" />
                      </RAButton>
                    </div>
                    <Separator className="pb-4" />
                  </div>
                  <>
                    {!isStorageConnected ? (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-6 px-8">
                        <div className="relative w-fit">
                          <div className="bg-theme-gradient-from/25 border-theme-gradient-from/25 absolute inset-0 rounded-full border-2" />
                          <div className="relative z-10 p-3">
                            <NotebookPen
                              size={24}
                              className="text-theme-gradient-from"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-3">
                          <span className="text-md text-center font-semibold">
                            Tilføj noter til dine leads
                          </span>
                          <span className="text-theme-on-background-variant text-center text-xs">
                            {isAdmin(user) ? (
                              <>
                                Med noter på leadkortet er du sikker på, at
                                ingen information går tabt. Forbind til OneDrive
                                eller Dropbox for at aktivere denne
                                funktionalitet.
                              </>
                            ) : (
                              <>
                                Med noter på leadkortet er du sikker på, at
                                ingen information går tabt. Kontakt din
                                salgsleder for at aktivere denne funkionalitet.
                              </>
                            )}
                          </span>
                          <Button
                            className="mt-4 h-11!"
                            onPress={() =>
                              navigate({ to: "/settings/integrations" })
                            }
                          >
                            Tilføj opbevaring
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="relative flex-1 overflow-hidden">
                          <div
                            className={twMerge(
                              "from-theme-background via-theme-background/80 to-theme-background/0 pointer-events-none absolute top-0 z-10 h-4 w-full bg-linear-to-b transition-opacity duration-300 md:h-12",
                              canScrollNotes && !isAtTop
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          <div
                            id="scroll-headers"
                            ref={setNotesScrollEl}
                            className="relative h-full overflow-y-scroll"
                          >
                            {[...notes].reverse().map((note, index) => (
                              <NoteItem
                                key={note.id}
                                note={note}
                                isFirst={index === 0}
                                isEditing={editId?.has(note.id) ?? false}
                                isOwner={user.id === note.userId}
                                onEdit={() => handleEditNote(note)}
                                onDelete={() => handleDeleteNote(note.id)}
                              />
                            ))}
                          </div>
                          <div
                            className={twMerge(
                              "from-theme-background/0 via-theme-background/80 to-theme-background pointer-events-none absolute -bottom-2 z-10 h-10 w-full bg-linear-to-b transition-opacity duration-300 md:h-20",
                              canScrollNotes && !isAtBottom
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                        </div>
                        <div className="relative mt-auto pt-6">
                          {!isWithinResponsiveRange ? (
                            <motion.div className="border-theme-outline-variant mx-8 rounded-sm border p-4 transition-all">
                              <TextArea
                                value={textareaValue}
                                onChange={(e) =>
                                  setTextareaValue(e.target.value)
                                }
                                placeholder="Skriv en note..."
                                className="field-sizing-content max-h-30 w-full resize-none overflow-auto rounded-sm text-sm font-semibold focus:outline-none"
                              />
                              <AnimatePresence>
                                {(textareaValue.trim().length > 0 ||
                                  editId) && (
                                  <motion.div
                                    className="mt-3 flex justify-center gap-3 px-3"
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 5 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <Button
                                      size="sm"
                                      intent="secondary"
                                      className="h-8 w-24.5 min-w-0!"
                                      onPress={() => {
                                        if (editId) {
                                          setEditId(null);
                                          setTextareaValue("");
                                        } else {
                                          setTextareaValue("");
                                        }
                                      }}
                                    >
                                      Fortryd
                                    </Button>
                                    <Button
                                      isDisabled={disableSaveNote}
                                      intent="primary"
                                      size="sm"
                                      className="h-8 w-20.5 min-w-0!"
                                      onPress={
                                        editId ? editingNote : sendNewNote
                                      }
                                    >
                                      Gem
                                    </Button>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.div>
                          ) : (
                            <div>
                              <Sheet
                                isOpen={openTextArea}
                                onOpenChange={setOpenTextArea}
                              >
                                <div className="flex w-full">
                                  <Button
                                    intent="primary"
                                    size="lg"
                                    className="mx-6 mb-4 h-8 w-full"
                                    animate
                                  >
                                    Tilføj note
                                  </Button>
                                </div>
                                <SheetContent side="bottom" className="px-8">
                                  <PageHeader
                                    actions={
                                      <AnimatePresence>
                                        {
                                          <motion.div
                                            className="flex justify-end gap-3 px-3"
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 5 }}
                                            transition={{ duration: 0.2 }}
                                          >
                                            <Button
                                              size="sm"
                                              intent="secondary"
                                              className="h-11 w-24.5 min-w-0!"
                                              onPress={() => {
                                                if (editId) {
                                                  setEditId(null);
                                                  setTextareaValue("");
                                                  setOpenTextArea(false);
                                                } else {
                                                  setTextareaValue("");
                                                  setOpenTextArea(false);
                                                }
                                              }}
                                            >
                                              Fortryd
                                            </Button>
                                            <Button
                                              isDisabled={disableSaveNote}
                                              intent="primary"
                                              size="sm"
                                              className="h-11 w-24.5 min-w-0!"
                                              onPress={
                                                editId
                                                  ? editingNote
                                                  : sendNewNote
                                              }
                                            >
                                              Gem
                                            </Button>
                                          </motion.div>
                                        }
                                      </AnimatePresence>
                                    }
                                    className="text-theme-on-glass flex items-center justify-between px-0! pt-8! text-xs font-medium uppercase"
                                  >
                                    Tilføj note
                                  </PageHeader>
                                  <SheetBody className="px-0! py-12!">
                                    <TextArea
                                      value={textareaValue}
                                      onChange={(e) =>
                                        setTextareaValue(e.target.value)
                                      }
                                      placeholder="Skriv en note..."
                                      className="text-theme-on-glass field-sizing-content max-h-[40vh] w-full resize-none overflow-auto rounded-sm text-sm font-semibold focus:outline-none"
                                    />
                                  </SheetBody>
                                </SheetContent>
                              </Sheet>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                </SidebarContent>
              </Sidebar>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {!search.embed && (
            <>
              {!notesOpen && (
                <motion.div
                  key="quick-actions"
                  initial={{ right: -52 }}
                  animate={{
                    right: 24,
                    transition: { duration: 0.35, delay: 0.5 },
                  }}
                  exit={{ right: -52 }}
                  className="fixed top-6 right-6 z-10 flex flex-col items-end justify-center gap-y-3"
                >
                  <Tooltip
                    tooltipText="Tilbage til listen af leads"
                    placement="left"
                  >
                    <RAButton
                      onPress={() => navigateBack()}
                      className="cursor-pointer rounded-full bg-white p-4 shadow-md dark:bg-black"
                    >
                      <X className="text-theme-on-background size-5" />
                    </RAButton>
                  </Tooltip>

                  {context.leadView && (
                    <>
                      <div className="relative">
                        <AnimatePresence>
                          {zoneMode && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="absolute right-0 flex h-[52px] max-w-fit min-w-[52px] items-center justify-center overflow-hidden rounded-full bg-black/25 p-4 pr-[70px] font-medium text-white transition-[width] duration-200"
                            >
                              {formattedCount}
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <Tooltip
                          tooltipText={
                            zoneMode
                              ? "Afslut fokus-tilstand"
                              : "Start 30 minutters fokuseret salgsarbejde!"
                          }
                          placement="left"
                        >
                          <RAButton
                            className="relative cursor-pointer rounded-full bg-white p-4 shadow-md dark:bg-black"
                            onPress={toggleZoneMode}
                          >
                            <AnimatePresence mode="popLayout">
                              {zoneMode ? (
                                <MotionClock
                                  initial={{ opacity: 0, rotate: -180 }}
                                  exit={{ opacity: 0, rotate: -180 }}
                                  animate={{
                                    rotate: 0,
                                    opacity: 1,
                                  }}
                                  className="size-5 text-amber-400"
                                />
                              ) : (
                                <MotionFullscreen
                                  initial={{ opacity: 0, rotate: -180 }}
                                  exit={{ opacity: 0, rotate: -180 }}
                                  animate={{
                                    rotate: 0,
                                    opacity: 1,
                                  }}
                                  className="text-theme-on-background size-5"
                                />
                              )}
                            </AnimatePresence>
                          </RAButton>
                        </Tooltip>
                      </div>
                    </>
                  )}

                  <Tooltip
                    tooltipText="Åbn noter for dette lead"
                    placement="left"
                  >
                    <div className="relative">
                      <RAButton
                        onPress={() => setNotesOpen(!notesOpen)}
                        className="cursor-pointer rounded-full bg-white p-4 shadow-md dark:bg-black"
                      >
                        <MotionNote className="text-theme-on-background size-5" />
                      </RAButton>
                      {notes.length > 0 && (
                        <div className="bg-theme-primary text-theme-glass absolute -top-1 -right-0.5 flex aspect-square min-h-5 min-w-5 items-center justify-center rounded-full px-0.5 text-[11px] leading-none font-medium">
                          {notes.length > 99 ? "99" : notes.length}
                        </div>
                      )}
                    </div>
                  </Tooltip>
                </motion.div>
              )}

              {context.leadView && (
                <motion.div
                  key="action-bar"
                  initial={{ height: 0 }}
                  animate={{
                    height: 96,
                    transition: { duration: 0.35, delay: 0.25 },
                  }}
                  exit={{ height: 0 }}
                >
                  <div className="colors-glass backdrop-blur-glass fixed inset-x-0 bottom-0 z-0 flex h-[inherit] items-center justify-between px-6 [view-transition-name:none]">
                    <Button
                      intent="tertiary"
                      size="lg"
                      className={
                        leadsIndex > 0 ? "" : "pointer-events-none invisible"
                      }
                      onPress={goToPreviousLead}
                    >
                      <ChevronLeft className="mr-2 size-5" />
                      Forrige
                    </Button>

                    <div className="grid *:col-start-1 *:row-start-1">
                      <AnimatePresence mode="sync">
                        {isEditor ||
                        isEditorInOtherSession ||
                        !!lead?.thirdPartyLink ? (
                          campaign &&
                          lead && (
                            <motion.div
                              key="action-bar"
                              className="flex items-center justify-center"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              <ActionBar
                                campaign={campaign}
                                currentLeadStatus={
                                  context.leadView.simpleLead.status
                                }
                                lead={lead.lead}
                                onAction={updateLeadStatus}
                                thirdPartyLink={lead?.thirdPartyLink}
                                disableActions={
                                  shouldExportLead && isFetchingContact
                                }
                              />
                            </motion.div>
                          )
                        ) : currentEditor ? (
                          <motion.div
                            key="editor-locked"
                            className="flex items-center justify-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            {currentEditor.user?.name ?? "En anden bruger"}{" "}
                            kigger på dette lead
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>

                    <Button
                      intent="tertiary"
                      size="lg"
                      className={
                        hasNextPage || leadsIndex < leads.length - 1
                          ? ""
                          : "pointer-events-none invisible"
                      }
                      isDisabled={leadsIndex === leads.length - 1}
                      onPress={() => goToNextLead(true)}
                    >
                      {leadsIndex === -1 ? "Start en ny session" : "Næste"}
                      <ChevronRight className="ml-2 size-5" />
                    </Button>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </AnimatePresence>
      </div>
      <Modal
        isOpen={openLeadActivityModal}
        key={lead?.lead.id + "-lead-activity-modal"}
        onOpenChange={setOpenLeadActivityModal}
        size="2xl"
        className="overflow-visible"
        overlayClassName="overflow-y-auto items-start text-theme-on-background"
        aria-label="Dialog opret lead aktivitet"
      >
        {isFetchingContact ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex h-[80dvh] w-full flex-col items-center justify-center gap-y-6">
              <Loader2 size={56} className="animate-spin" />
              <span className="animate-pulse">Venter på kontaktperson...</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <IntegrationForm
              className="mt-0"
              size="sm"
              inputs={inputs!} // guaranteed by showActivityDialog
              defaultValues={campaign?.exportInput ?? undefined}
              includeVisible={false}
              showSkip={
                campaign?.exportInput?.createengagementdropdown === "optional"
              }
              onSkip={() => {
                setOpenLeadActivityModal(false);
                updateLeadStatus(
                  temporarilyStoreLeadActionParams!.status,
                  temporarilyStoreLeadActionParams!.action,
                  temporarilyStoreLeadActionParams!.date,
                  temporarilyStoreLeadActionParams!.feedback,
                  undefined,
                  true,
                );
                setIsSubmittingLeadActivity(false);
              }}
              onSubmit={async (data) => {
                if (!temporarilyStoreLeadActionParams) {
                  toast.error("Noget gik galt, prøv igen");
                  return;
                }
                setOpenLeadActivityModal(false);
                updateLeadStatus(
                  temporarilyStoreLeadActionParams.status,
                  temporarilyStoreLeadActionParams.action,
                  temporarilyStoreLeadActionParams.date,
                  temporarilyStoreLeadActionParams.feedback,
                  data,
                  true,
                );
                setIsSubmittingLeadActivity(false);
              }}
              disableSubmit={isSubmittingLeadActivity}
              submitLabel={
                isSubmittingLeadActivity ? "Overfører lead..." : "Gem"
              }
            />
          </div>
        )}
      </Modal>
    </LeadNavigationProvider>
  );
}
